/**
 * Meetings → All meetings (screenshots/117–118, brief §2.20): every meeting by Chicago day (Today marked, LIVE when in
 * session), Upcoming / Past / All bodies / Has a vote, follow a body; or the month grid. Pure helpers in
 * meetingsCalendar.ts (from the starter). Rows come from the Following snapshot's meetings (all bodies in its window).
 */
import { useMemo, useRef, useState } from "react";
import { CalendarDays, Check, ChevronDown, Landmark, List, Plus, Search } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { List as RowList, ListRow, RowLead, RowMain, RowTitle } from "../components/shared/List";
import { useShallow } from "zustand/react/shallow";
import { useFollowing } from "../stores/followingStore";
import { followsStore, useFollows } from "../stores/followsStore";
import { MiniCalendar } from "../components/following/FollowingSide";
import { useSlashToFocus } from "../hooks/useShortcuts";
import { onTablistKey } from "../hooks/useTabs";
import type { Follow, MeetingSummary } from "../types";
import { dayKey, filterMeetings, groupByDay, monthGrid, timeLabel, type MeetingFilters, type MeetingLite } from "./meetingsCalendar";

/** A body name as follows store it ("Committee on Finance" and "Finance" match). */
export const norm = (s: string) => s.toLowerCase().replace(/^committee on /, "").replace(/&/g, "and").replace(/[^a-z0-9]+/g, " ").trim();
const short = (body: string) => body.replace(/^Committee on (the )?/, "").replace(/ and /g, " & ");
const WINDOW_DAYS = 7;

const lite = (m: MeetingSummary, liveId: string | null, now: Date): MeetingLite => ({
  id: m.id, body: m.body, kind: m.bodyKind, startsAt: m.startsAt, location: m.location ?? "", items: m.items ?? null, hasVote: !!m.hasVote,
  status: m.id === liveId || m.status === "live" ? "live" : dayKey(m.startsAt) < dayKey(now) ? "past" : "upcoming",
});

export function AllMeetings({ now = new Date() }: { now?: Date }) {
  const { meetings, matchIdsByMeeting, liveId } = useFollowing(useShallow((s) => ({ meetings: s.meetings, matchIdsByMeeting: s.matchIdsByMeeting, liveId: s.live?.meetingId ?? null })));
  const follows = useFollows((s) => s.follows);
  const bodyFollows = useMemo(() => follows.filter((f) => f.type === "committee"), [follows]);
  const followOf = (body: string): Follow | undefined => bodyFollows.find((f) => norm(f.refId || f.label) === norm(body));
  const all = useMemo(() => meetings.map((m) => lite(m, liveId, now)), [meetings, liveId, now]);
  const bodies = useMemo(() => [...new Set(all.map((m) => m.body))].sort(), [all]);

  const [view, setView] = useState<"list" | "calendar">("list");
  const [f, setF] = useState<MeetingFilters>({ when: "upcoming" });
  const [weeks, setWeeks] = useState(1);
  const search = useRef<HTMLInputElement>(null);
  useSlashToFocus(search);

  const shown = useMemo(() => filterMeetings(all, f, now), [all, f, now]);
  // Upcoming pages a week at a time ("Load the next two weeks"); Past shows everything in the snapshot.
  const until = new Date(now.getTime() + weeks * WINDOW_DAYS * 864e5);
  const paged = f.when === "upcoming" && !f.q ? shown.filter((m) => m.status === "live" || dayKey(m.startsAt) < dayKey(until)) : shown;
  const today = dayKey(now);
  const m = { year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7)) };
  const monthLabel = new Date(Date.UTC(m.year, m.month - 1, 15)).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  const byDay = useMemo(() => { const g = new Map<string, MeetingLite[]>(); for (const x of all) { const k = dayKey(x.startsAt); g.set(k, [...(g.get(k) ?? []), x]); } return g; }, [all]);
  const followedUpcoming = useMemo(() => new Set(all.filter((x) => x.status !== "past" && (matchIdsByMeeting[x.id]?.length || followOf(x.body))).map((x) => dayKey(x.startsAt))), [all, matchIdsByMeeting, bodyFollows]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (body: string) => {
    const existing = followOf(body);
    if (existing) void followsStore.getState().unfollow(existing.id);
    else void followsStore.getState().follow({ type: "committee", label: body, refId: body });
  };
  const pick = (day: string) => requestAnimationFrame(() => document.getElementById(`day-${day}`)?.scrollIntoView({ block: "start" }));
  const range = (() => {
    if (!paged.length) return null;
    const a = new Date(`${dayKey(paged[0].startsAt)}T12:00:00Z`), b = new Date(`${dayKey(paged[paged.length - 1].startsAt)}T12:00:00Z`);
    const mo = (d: Date) => d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    return +a === +b ? `${mo(a)} ${a.getUTCDate()}` : mo(a) === mo(b) ? `${mo(a)} ${a.getUTCDate()}–${b.getUTCDate()}` : `${mo(a)} ${a.getUTCDate()} – ${mo(b)} ${b.getUTCDate()}`;
  })();

  return (
    <section className="all-meetings">
      <div className="filter-bar all-meetings__bar">
        <label className="follow-search all-meetings__search">
          <Search size={15} aria-hidden />
          <input ref={search} type="search" aria-label="Search meetings and agendas" placeholder="Search meetings and agendas" value={f.q ?? ""} onChange={(e) => setF({ ...f, q: e.target.value })} aria-keyshortcuts="/" />
          <kbd>/</kbd>
        </label>
        {view === "list"
          ? (["upcoming", "past"] as const).map((w) => <button key={w} className="am-toggle" aria-pressed={f.when === w} onClick={() => setF({ ...f, when: w })}>{w === "upcoming" ? "Upcoming" : "Past"}</button>)
          : <span className="am-toggle am-toggle--month" aria-current="date">{monthLabel}</span>}
        <Dropdown className="am-select" label="Body" value={f.bodies?.[0] ?? ""} onChange={(v) => setF({ ...f, bodies: v ? [v] : undefined })}
          chevron={<ChevronDown size={14} aria-hidden />} options={[{ value: "", label: "All bodies" }, ...bodies.map((b) => ({ value: b, label: b }))]} />
        <button className="am-toggle" aria-pressed={!!f.hasVote} onClick={() => setF({ ...f, hasVote: !f.hasVote })}>Has a vote</button>
        <span className="spacer" />
        <div className="view-switch" role="tablist" aria-label="View" onKeyDown={onTablistKey}>
          <button role="tab" aria-selected={view === "list"} onClick={() => setView("list")}><List size={15} aria-hidden />List</button>
          <button role="tab" aria-selected={view === "calendar"} onClick={() => setView("calendar")}><CalendarDays size={15} aria-hidden />Calendar</button>
        </div>
      </div>

      {view === "list" ? (
        <div className="all-meetings__body">
          <div className="meeting-days">
            {groupByDay(paged, now).map((d) => (
              <section key={d.key} id={`day-${d.key}`} aria-labelledby={`d-${d.key}`}>
                <h3 id={`d-${d.key}`}>{d.label}{d.today && <span className="chip chip--neutral">Today</span>}</h3>
                <ul>{d.meetings.map((x) => {
                  const n = matchIdsByMeeting[x.id]?.length ?? 0, fol = followOf(x.body);
                  return (
                    <li key={x.id}>
                      <time dateTime={x.startsAt}>{timeLabel(x.startsAt)}</time>
                      <span className="meeting-days__main">
                        <a href={`/dashboard/meetings/${encodeURIComponent(x.id)}`}><b>{x.body}</b></a>{x.status === "live" && <span className="live-chip"><i />LIVE</span>}
                        <small>{[x.kind === "committee" ? "Committee" : null, x.location || null, x.items != null ? `${x.items} items` : null].filter(Boolean).join(" · ")}</small>
                      </span>
                      <span className="meeting-days__side">
                        {n > 0 && <span className="am-count">{n} you follow{x.hasVote ? " · votes" : ""}</span>}
                        {x.kind === "committee" && (
                          <button className={`btn btn--secondary btn--small am-follow${fol ? " is-on" : ""}`} aria-pressed={!!fol} onClick={() => toggle(x.body)}>
                            {fol ? <Check size={14} aria-hidden /> : <Plus size={14} aria-hidden />}{fol ? "Following" : "Follow"}</button>
                        )}
                      </span>
                    </li>);
                })}</ul>
              </section>))}
            {paged.length === 0 && <p className="muted">No meetings match.</p>}
            {range && f.when === "upcoming" && !f.q && (
              <p className="meeting-days__foot">Showing {range} · {paged.length} of {shown.length} upcoming meetings
                {paged.length < shown.length && <> · <button className="link-button" onClick={() => setWeeks((w) => w + 2)}>Load the next two weeks</button></>}</p>
            )}
          </div>
          <aside className="am-side">
            {bodyFollows.length > 0 && (
              <section className="side-list am-bodies">
                <h2>Bodies you follow · {bodyFollows.length}</h2>
                <RowList variant="plain" className="am-bodies__list">{bodyFollows.map((b) => (
                  <ListRow key={b.id} className="am-bodies__row" wrap={false}>
                    <RowLead><Landmark size={15} aria-hidden /></RowLead>
                    <RowMain min={0}><RowTitle lines={2} className="am-bodies__name">{b.label}</RowTitle></RowMain>
                  </ListRow>
                ))}</RowList>
                <p className="muted">Their meetings appear in Following with why each one matters to you.</p>
              </section>
            )}
            <MiniCalendar meetings={meetings} followedUpcoming={followedUpcoming} onPick={pick} />
          </aside>
        </div>
      ) : (
        <>
          <table className="month" aria-label={monthLabel}>
            <thead><tr>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <th key={d} scope="col">{d}</th>)}</tr></thead>
            <tbody>{monthGrid(m.year, m.month).map((w, i) => (
              <tr key={i}>{w.map((d) => (
                <td key={d.key} data-out={!d.inMonth || undefined} data-today={d.key === today || undefined} data-followed={(d.key > today && followedUpcoming.has(d.key)) || undefined}>
                  <span className="month__day">{d.day}</span>
                  {(byDay.get(d.key) ?? []).filter((x) => (!f.hasVote || x.hasVote) && (!f.bodies?.length || f.bodies.includes(x.body))).map((x) => (
                    <a key={x.id} href={`/dashboard/meetings/${encodeURIComponent(x.id)}`} title={x.body}
                      className={`month__pill is-${x.kind}${followOf(x.body) || matchIdsByMeeting[x.id]?.length ? " is-followed" : ""}`}>{short(x.body)}</a>))}
                </td>))}</tr>))}</tbody>
          </table>
          <p className="month__legend"><span><i className="is-council" />City Council</span><span><i className="is-committee" />Committee</span><span><i className="is-dot" />A body you follow</span></p>
        </>
      )}
    </section>
  );
}
